// Cota de geração de roteiro com IA — lado cliente (só pra UX).
//
// O contador `aiTripsUsed` e o override `enabled` (acesso ilimitado) vivem em
// `aiAccess/{uid}`; a cota grátis global vive em `appConfig/ai.freeTrips`.
// Ambos os docs são escritos SÓ pelo backend; as Security Rules deixam o
// usuário LER o próprio `aiAccess` e o `appConfig`. `getAiQuota` lê os dois
// pra mostrar a contagem certa na badge do card "Gerar com IA".
//
// Limite efetivo: aiAccess.aiTripsLimit (se ≥ 1) > appConfig/ai.freeTrips >
// AI_FREE_TRIPS (fallback). `enabled === true` = ilimitado.
//
// IMPORTANTE: o gate de custo de verdade é server-side, em
// /api/ai/generate-trip. Este check é só pra UX — por isso, em caso de falha
// de leitura, assume cota cheia (fail-open): o servidor ainda barra se a cota
// estiver de fato esgotada.

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../core/firebase.js';
import { AI_FREE_TRIPS } from './aiQuota.js';

/**
 * Lê `aiAccess/{uid}` + `appConfig/ai` e devolve o estado da cota de IA.
 *
 * @param {string} uid
 * @returns {Promise<{ used:number, remaining:number, limit:number,
 *   unlimited:boolean, allowed:boolean }>}
 */
export async function getAiQuota(uid) {
  const full = {
    used: 0, remaining: AI_FREE_TRIPS, limit: AI_FREE_TRIPS,
    unlimited: false, allowed: true,
  };
  if (!uid) return full;
  try {
    const [accSnap, cfgSnap] = await Promise.all([
      getDoc(doc(db, 'aiAccess', uid)),
      // appConfig/ai pode não existir ainda — falha aqui não derruba a cota
      // (cai no fallback AI_FREE_TRIPS).
      getDoc(doc(db, 'appConfig', 'ai')).catch(() => null),
    ]);
    const data = accSnap.exists() ? (accSnap.data() || {}) : {};
    const cfg = cfgSnap && cfgSnap.exists() ? (cfgSnap.data() || {}) : {};

    const unlimited = data.enabled === true;
    const used = Number(data.aiTripsUsed) || 0;

    // Limite efetivo: config global → fallback; override individual sobrepõe.
    let limit = Number(cfg.freeTrips);
    if (!Number.isFinite(limit) || limit < 0) limit = AI_FREE_TRIPS;
    const perUser = Number(data.aiTripsLimit) || 0;
    if (perUser >= 1) limit = perUser;

    const remaining = unlimited ? Infinity : Math.max(0, limit - used);
    return { used, remaining, limit, unlimited, allowed: unlimited || remaining > 0 };
  } catch (e) {
    console.warn('[aiQuota] leitura falhou — tratando como cota cheia:', e?.message || e);
    return full;
  }
}
